/**
 * Equipment / device management for a practice site — backed by FHIR Device.
 */
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useDisclosure } from '@mantine/hooks';
import {
  IconChevronDown,
  IconChevronRight,
  IconPlus,
} from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';
import { equipmentSchema } from '@/schemas';
import type { Room } from './RoomManagementSection';
import {
  LOCATION_RESOURCE_TABLE_COL_WIDTHS,
  locationResourceTableStyle,
} from './locationResourceTableLayout';

export type Portability = 'fixed' | 'portable';
export type EquipmentStatus = 'active' | 'inactive';

export interface Equipment {
  id: string;
  name: string;
  portability: Portability;
  /** Required when portability is fixed */
  homeRoomId?: string;
  status: EquipmentStatus;
}

const emptyEquipmentForm = (): Omit<Equipment, 'id'> => ({
  name: '',
  portability: 'portable',
  homeRoomId: undefined,
  status: 'active',
});

function getErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'outcome' in err) {
    const issue = (err as {
      outcome?: { issue?: Array<{ diagnostics?: string; details?: { text?: string } }> };
    }).outcome?.issue?.[0];
    if (issue?.diagnostics) return issue.diagnostics;
    if (issue?.details?.text) return issue.details.text;
  }
  return err instanceof Error ? err.message : 'Failed to delete equipment';
}

export interface EquipmentManagementSectionProps {
  therapyRooms: Room[];
  equipment: Equipment[];
  canEdit: boolean;
  onSave: (editingId: string | null, data: Omit<Equipment, 'id'>) => Promise<void>;
  onDelete: (equipmentId: string) => Promise<void>;
  onToggleStatus: (equipmentId: string, active: boolean) => Promise<void>;
}

export function EquipmentManagementSection({
  therapyRooms,
  equipment,
  canEdit,
  onSave,
  onDelete,
  onToggleStatus,
}: EquipmentManagementSectionProps): JSX.Element {
  const [opened, { toggle }] = useDisclosure(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Omit<Equipment, 'id'>>(emptyEquipmentForm());
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  const roomOptions = useMemo(
    () => therapyRooms.filter((r) => r.type === 'therapy-room' && r.status === 'active'),
    [therapyRooms],
  );
  const roomNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of roomOptions) m.set(r.id, r.name);
    return m;
  }, [roomOptions]);

  const openAdd = useCallback(() => {
    setEditingId(null);
    setFormErrors({});
    setSaveError(null);
    const next = emptyEquipmentForm();
    if (roomOptions[0] && next.portability === 'fixed') {
      next.homeRoomId = roomOptions[0].id;
    }
    setForm(next);
    setModalOpen(true);
  }, [roomOptions]);

  const openEdit = useCallback((eq: Equipment) => {
    setEditingId(eq.id);
    setFormErrors({});
    setSaveError(null);
    setForm({
      name: eq.name,
      portability: eq.portability,
      homeRoomId: eq.homeRoomId,
      status: eq.status,
    });
    setModalOpen(true);
  }, []);

  const handleSave = useCallback(async () => {
    setSaveError(null);
    const result = equipmentSchema.safeParse(form);
    if (!result.success) {
      const errors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0]?.toString() ?? '_root';
        errors[key] = issue.message;
      }
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    const { name, portability, homeRoomId, status } = result.data;

    const payload: Omit<Equipment, 'id'> = {
      name,
      portability,
      homeRoomId: portability === 'fixed' ? homeRoomId : undefined,
      status,
    };

    setSaving(true);
    try {
      await onSave(editingId, payload);
      setModalOpen(false);
      setEditingId(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save equipment');
    } finally {
      setSaving(false);
    }
  }, [editingId, form, onSave]);

  const confirmDelete = useCallback(async () => {
    if (!confirmDeleteId) return;
    setSaving(true);
    try {
      await onDelete(confirmDeleteId);
      setConfirmDeleteId(null);
    } catch (err) {
      notifications.show({
        title: 'Delete blocked',
        message: getErrorMessage(err),
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  }, [confirmDeleteId, onDelete]);

  const counts = {
    fixed: equipment.filter((e) => e.status !== 'inactive' && e.portability === 'fixed').length,
    portable: equipment.filter((e) => e.status !== 'inactive' && e.portability === 'portable').length,
  };

  return (
    <>
      <Stack gap="xs" mt="md" pt="md" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
        <Group justify="space-between" wrap="nowrap" style={{ width: '100%' }}>
          <UnstyledButton type="button" onClick={toggle} style={{ flex: 1, minWidth: 0 }}>
            <Group gap="xs" wrap="nowrap">
              {opened ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
              <Text size="sm" fw={600}>
                Equipment
              </Text>
              <Group gap={6}>
                <Badge size="xs" variant="light" color="blue">
                  {counts.fixed} fixed
                </Badge>
                <Badge size="xs" variant="light" color="violet">
                  {counts.portable} portable
                </Badge>
              </Group>
            </Group>
          </UnstyledButton>
          {canEdit && (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={openAdd}
            >
              Add equipment
            </Button>
          )}
        </Group>

        {opened && (
          <>
            {equipment.length === 0 ? (
              <Text size="sm" c="gray.6" py="sm">
                No equipment yet. Add fixed or portable devices for this location.
              </Text>
            ) : (
              <Table highlightOnHover mt="xs" style={locationResourceTableStyle}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={LOCATION_RESOURCE_TABLE_COL_WIDTHS.name}>Name</Table.Th>
                    <Table.Th w={LOCATION_RESOURCE_TABLE_COL_WIDTHS.typePortability}>
                      Portability
                    </Table.Th>
                    <Table.Th w={LOCATION_RESOURCE_TABLE_COL_WIDTHS.capacityAssignedRoom}>
                      Assigned room
                    </Table.Th>
                    <Table.Th w={LOCATION_RESOURCE_TABLE_COL_WIDTHS.status}>Status</Table.Th>
                    {canEdit && <Table.Th w={LOCATION_RESOURCE_TABLE_COL_WIDTHS.actions} />}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {equipment.map((eq) => (
                    <Table.Tr
                      key={eq.id}
                      onClick={() => canEdit && openEdit(eq)}
                      style={{
                        opacity: eq.status === 'inactive' ? 0.55 : 1,
                        cursor: canEdit ? 'pointer' : 'default',
                      }}
                    >
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {eq.name}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="sm" color={eq.portability === 'fixed' ? 'blue' : 'grape'} variant="outline">
                          {eq.portability === 'fixed' ? 'Fixed' : 'Portable'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {eq.portability === 'fixed' && eq.homeRoomId
                            ? roomNameById.get(eq.homeRoomId) ?? eq.homeRoomId
                            : '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Group gap="xs" wrap="nowrap">
                          <Switch
                            size="xs"
                            disabled={!canEdit}
                            checked={eq.status === 'active'}
                            onChange={(e) => void onToggleStatus(eq.id, e.currentTarget.checked)}
                            aria-label={
                              eq.status === 'active' ? `Deactivate ${eq.name}` : `Activate ${eq.name}`
                            }
                            styles={{ track: { cursor: canEdit ? 'pointer' : 'default' } }}
                          />
                          {eq.status === 'inactive' && (
                            <Badge size="xs" color="gray" variant="outline">
                              Inactive
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                      {canEdit && (
                        <Table.Td>
                          <Group gap={4} justify="flex-end">
                            <Button
                              size="xs"
                              variant="light"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEdit(eq);
                              }}
                              aria-label={`Edit ${eq.name}`}
                            >
                              Edit
                            </Button>
                          </Group>
                        </Table.Td>
                      )}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </>
        )}
      </Stack>

      <Modal
        opened={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingId(null);
        }}
        title={editingId ? 'Edit equipment' : 'Add equipment'}
        size="md"
      >
        <Stack gap="md">
          {saveError && (
            <Alert color="red" withCloseButton onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          )}
          <TextInput
            label="Name"
            placeholder="e.g., Meta Quest 3 #2"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            error={formErrors.name}
            required
          />
          <Select
            label="Portability"
            data={[
              { value: 'portable', label: 'Portable (reserve when scheduling)' },
              { value: 'fixed', label: 'Fixed (assigned to a room)' },
            ]}
            value={form.portability}
            onChange={(v) =>
              setForm((f) => {
                const p = (v as Portability) || 'portable';
                return {
                  ...f,
                  portability: p,
                  homeRoomId: p === 'fixed' ? f.homeRoomId ?? roomOptions[0]?.id : undefined,
                };
              })
            }
          />
          {form.portability === 'fixed' && (
            <Select
              label="Home room"
              placeholder="Select a room"
              data={roomOptions.map((r) => ({ value: r.id, label: r.name }))}
              value={form.homeRoomId ?? null}
              onChange={(v) => setForm((f) => ({ ...f, homeRoomId: v ?? undefined }))}
              error={formErrors.homeRoomId}
              required
            />
          )}
          <Select
            label="Status"
            data={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
            value={form.status}
            onChange={(v) =>
              setForm((f) => ({ ...f, status: (v as EquipmentStatus) || 'active' }))
            }
          />
          <Group justify={editingId ? 'space-between' : 'flex-end'} mt="md">
            {editingId && (
              <Button
                variant="subtle"
                color="red"
                onClick={() => {
                  setModalOpen(false);
                  setConfirmDeleteId(editingId);
                }}
              >
                Delete
              </Button>
            )}
            <Group gap="sm">
              <Button variant="outline" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button
                loading={saving}
                onClick={() => void handleSave()}
              >
                {editingId ? 'Save' : 'Add'}
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        title="Delete equipment"
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            Are you sure you want to delete{' '}
            <Text span fw={600}>
              {equipment.find((e) => e.id === confirmDeleteId)?.name}
            </Text>
            ? This cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="outline" onClick={() => setConfirmDeleteId(null)}>
              Cancel
            </Button>
            <Button color="red" loading={saving} onClick={() => void confirmDelete()}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
